// src/screens/StreamViewScreen.js - VERSÃO CORRIGIDA
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
  ActivityIndicator,
  Platform
} from 'react-native';
import {
  Appbar,
  Card,
  Title,
  Paragraph,
  Text,
  ProgressBar,
  Chip,
  Badge,
  Button,
  Surface
} from 'react-native-paper';
import { AVAQualisTheme } from '../theme/theme';
import FooterComponent from '../components/FooterComponent';
import StreamAttachmentComponent from '../components/StreamAttachmentComponent';
import { pollStreamUpdates, getStream } from '../lib/api';

export default function StreamViewScreen({ route, navigation }) {
  const { streamId } = route.params;
  
  // Estados
  const [streamData, setStreamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isPolling, setIsPolling] = useState(true);
  
  // Refs
  const pollingRef = useRef(null);
  const isMountedRef = useRef(true);

  // Adicione este estado após os outros estados:
const [connectionError, setConnectionError] = useState(false);
const reconnectTimeoutRef = useRef(null);
  
  // Função para buscar dados iniciais do stream
// Função para buscar dados iniciais do stream - VERSÃO MELHORADA
const loadStreamData = async () => {
  try {
    console.log('📡 [StreamView] Carregando dados do stream:', streamId);
    
    const data = await getStream(streamId);
    
    if (!data) {
      throw new Error('Stream não encontrado');
    }
    
    console.log('✅ [StreamView] Dados do stream carregados:', {
      streamId: data.streamId,
      active: data.active,
      hasCurrentData: !!data.currentData,
      viewCount: data.viewCount
    });
    
    if (isMountedRef.current) {
      setStreamData(data);
      setLastUpdate(data.lastUpdate);
      setError(null);
      setConnectionError(false);
    }
    
  } catch (err) {
    console.error('❌ [StreamView] Erro ao carregar stream:', err);
    
    if (isMountedRef.current) {
      if (err.message.includes('404') || err.message.includes('não encontrado')) {
        setError('Stream não encontrado ou expirado');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Erro de conexão. Verifique sua internet.');
        setConnectionError(true);
      } else {
        setError(err.message || 'Erro ao carregar stream');
      }
    }
  } finally {
    if (isMountedRef.current) {
      setLoading(false);
      setRefreshing(false);
    }
  }
};
  
  // Função de polling para atualizações
// Modifique a função startPolling:
  
// Função de polling para atualizações - VERSÃO MELHORADA
const startPolling = async () => {
  if (!isPolling || !isMountedRef.current) return;
  
  try {
    const result = await pollStreamUpdates(streamId, lastUpdate);
    
    if (result.hasUpdate && isMountedRef.current) {
      console.log('📥 [StreamView] Nova atualização recebida');
      
      setStreamData(result);
      setLastUpdate(result.lastUpdate);
      setConnectionError(false);
      
      // Log de debug para verificar dados
      if (result.currentData) {
        console.log('📦 [StreamView] Dados atualizados:', {
          hasQuestion: !!result.currentData.currentQuestion,
          questionStatus: result.currentData.questionStatus,
          progress: result.currentData.progress?.percentage
        });
      }
    }
    
  } catch (err) {
    console.error('❌ [StreamView] Erro no polling:', err);
    
    // Se for erro de conexão, marcar e tentar reconectar
    if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
      setConnectionError(true);
      
      // Tentar reconectar em 5 segundos
      if (isMountedRef.current && isPolling) {
        reconnectTimeoutRef.current = setTimeout(() => {
          setConnectionError(false);
          startPolling();
        }, 5000);
      }
      return;
    }
  }
  
  // Continuar polling se ainda estiver montado e ativo
  if (isMountedRef.current && isPolling && !connectionError) {
    pollingRef.current = setTimeout(() => {
      startPolling();
    }, 1000);
  }
};


// Renderizar aviso de reconexão se houver erro de conexão
{connectionError && !error && (
  <View style={{
    backgroundColor: AVAQualisTheme.colors.warning + '20',
    padding: AVAQualisTheme.spacing.md,
    margin: AVAQualisTheme.spacing.md,
    borderRadius: AVAQualisTheme.borderRadius.md,
    borderLeftWidth: 4,
    borderLeftColor: AVAQualisTheme.colors.warning
  }}>
    <Text style={{
      fontSize: 14,
      color: AVAQualisTheme.colors.onSurface,
      fontWeight: '600'
    }}>
      ⚠️ Conexão perdida. Tentando reconectar...
    </Text>
  </View>
)}
  

  // Efeito para carregar dados iniciais
  useEffect(() => {
    isMountedRef.current = true;
    loadStreamData();
    
    return () => {
    isMountedRef.current = false;
    setIsPolling(false);
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  };
}, [streamId]);
  
  // Efeito para iniciar polling após carregar dados
  useEffect(() => {
    if (!loading && !error && streamData) {
      startPolling();
    }
    
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [loading, error, streamData, isPolling]);
  
  // Função para refresh manual
  const onRefresh = () => {
    setRefreshing(true);
    loadStreamData();
  };
  
  // Função para obter cor e texto do status da resposta
  const getAnswerDisplay = (answer) => {
    switch (answer) {
      case 'conforme':
        return { text: '✅ Conforme', color: AVAQualisTheme.colors.success };
      case 'nao-conforme':
        return { text: '❌ Não Conforme', color: AVAQualisTheme.colors.error };
      case 'nao-se-aplica':
        return { text: '➖ Não se Aplica', color: AVAQualisTheme.colors.warning };
      default:
        return { text: '⏳ Aguardando resposta...', color: AVAQualisTheme.colors.outline };
    }
  };
  
  // Função para obter status da questão
  const getQuestionStatusDisplay = (status) => {
    switch (status) {
      case 'editing':
        return '✏️ Editando...';
      case 'saving':
        return '💾 Salvando...';
      case 'saved':
        return '✅ Salvo';
      case 'selecting':
        return '👆 Selecionando próxima questão...';
      default:
        return '⏳ Aguardando...';
    }
  };
  
  // Função para formatar data de expiração
  const formatExpirationTime = (expiresAt) => {
    if (!expiresAt) return '';
    
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    
    if (diff <= 0) return 'Expirado';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `Expira em ${hours}h ${minutes}min`;
  };
  
  // Renderizar tela de loading
  if (loading) {
    return (
      <View style={styles.container}>
        {/* Header Simplificado para Loading */}
        <Appbar.Header style={styles.appbar}>
          <Appbar.BackAction 
            onPress={() => navigation.goBack()} 
            color={AVAQualisTheme.colors.onPrimary}
          />
          <Appbar.Content 
            title="🎥 Stream AVAQualis.com" 
            titleStyle={styles.appbarTitle}
          />
        </Appbar.Header>
        
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
          <Text style={styles.loadingText}>🎥 Conectando ao stream...</Text>
        </View>
        <FooterComponent />
      </View>
    );
  }
  
  // Renderizar tela de erro
  if (error) {
    return (
      <View style={styles.container}>
        {/* Header Simplificado para Erro */}
        <Appbar.Header style={styles.appbar}>
          <Appbar.BackAction 
            onPress={() => navigation.goBack()} 
            color={AVAQualisTheme.colors.onPrimary}
          />
          <Appbar.Content 
            title="🎥 Stream AVAQualis.com" 
            titleStyle={styles.appbarTitle}
          />
        </Appbar.Header>
        
        <View style={styles.errorContainer}>
          <Text style={styles.errorIcon}>❌</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button 
            mode="contained" 
            onPress={loadStreamData}
            style={styles.retryButton}
            icon="refresh"
          >
            Tentar Novamente
          </Button>
        </View>
        <FooterComponent />
      </View>
    );
  }
  
  // Renderizar stream
  const { currentData } = streamData || {};
  const { currentQuestion, progress } = currentData || {};
  
  return (
    <View style={styles.container}>
      {/* Header Customizado para Stream */}
      <Appbar.Header style={styles.appbar}>
        <Appbar.BackAction 
          onPress={() => navigation.goBack()} 
          color={AVAQualisTheme.colors.onPrimary}
        />
        <Appbar.Content 
          title="🎥 Stream AVAQualis.com" 
          subtitle={streamData?.formTitle || 'Visualizando avaliação'}
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />
        {streamData?.active && (
          <Badge style={styles.liveBadge}>AO VIVO</Badge>
        )}
      </Appbar.Header>
      
      <ScrollView 
        style={styles.scrollContainer}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[AVAQualisTheme.colors.primary]}
          />
        }
      >
        {/* Header do Stream */}
        <Surface style={styles.streamHeader}>
          <View style={styles.streamInfo}>
            <Text style={styles.streamTitle}>
              🎥 Stream da Avaliação
            </Text>
            <Text style={styles.formTitle}>{streamData?.formTitle}</Text>
            <View style={styles.avaliadorInfo}>
              <Text style={styles.avaliadorLabel}>Avaliador:</Text>
              <Text style={styles.avaliadorName}>
                {streamData?.avaliador?.nome}
              </Text>
            </View>
            <View style={styles.streamMeta}>
              <Chip 
                mode="outlined" 
                icon={streamData?.active ? "circle" : "circle-outline"}
                style={[
                  styles.statusChip,
                  streamData?.active && styles.statusChipActive
                ]}
              >
                {streamData?.active ? 'AO VIVO' : 'OFFLINE'}
              </Chip>
              <Text style={styles.expirationText}>
                {formatExpirationTime(streamData?.expiresAt)}
              </Text>
              <Text style={styles.viewCount}>
                👁️ {streamData?.viewCount || 0} visualizações
              </Text>
            </View>
          </View>
        </Surface>
        
        {/* Progresso Geral */}
        <Card style={styles.progressCard}>
          <Card.Content>
            <View style={styles.progressHeader}>
              <View style={styles.progressInfo}>
                <Text style={styles.progressText}>
                  📊 {progress?.percentage || 0}% concluído
                </Text>
                <Text style={styles.progressSubtext}>
                  {progress?.answeredCount || 0} de {progress?.totalQuestions || 0} respondidas
                </Text>
              </View>
              <Badge style={styles.progressBadge}>
                {progress?.answeredCount || 0}/{progress?.totalQuestions || 0}
              </Badge>
            </View>
            
            <ProgressBar 
              progress={(progress?.percentage || 0) / 100} 
              color={AVAQualisTheme.colors.primary}
              style={styles.progressBar}
            />
          </Card.Content>
        </Card>
        
        {/* Navegação de Questões */}
        {progress?.allQuestions && progress.allQuestions.length > 0 && (
          <Card style={styles.navigationCard}>
            <Card.Content>
              <Text style={styles.navigationTitle}>📋 Questões do Formulário</Text>
              <ScrollView 
                horizontal 
                showsHorizontalScrollIndicator={false} 
                style={styles.questionNavScroll}
              >
                {progress.allQuestions.map((q, index) => (
                  <Chip
                    key={q.id}
                    mode={q.isAnswered ? 'flat' : 'outlined'}
                    style={[
                      styles.questionChip,
                      q.isAnswered && styles.questionChipAnswered,
                      currentQuestion?.id === q.id && styles.questionChipActive
                    ]}
                  >
                    {q.isAnswered ? '✅' : '⏳'} {q.codigo}
                  </Chip>
                ))}
              </ScrollView>
            </Card.Content>
          </Card>
        )}
        
        {/* Questão Atual */}
        {currentQuestion ? (
          <Card style={styles.questionCard}>
            <Card.Content>
              <View style={styles.questionHeader}>
                <Chip 
                  mode="outlined" 
                  compact
                  style={styles.questionCodeChip}
                >
                  📋 {currentQuestion.codigo}
                </Chip>
                {currentQuestion.isEssential && (
                  <View style={styles.essentialBadge}>
                    <Text style={styles.essentialIcon}>⚠️</Text>
                    <Text style={styles.essentialText}>RE</Text>
                  </View>
                )}
                <Text style={styles.questionStatus}>
                  {getQuestionStatusDisplay(currentData?.questionStatus)}
                </Text>
              </View>
              
              <Title style={styles.questionTitle}>
                <Text style={styles.requirementLabel}>REQUISITO:</Text> {currentQuestion.question}
              </Title>
              
              {/* Detalhes da Questão */}
              {currentQuestion.premissa && (
                <View style={styles.detailsContainer}>
                  <Text style={styles.detailsLabel}>Premissa:</Text>
                  <Text style={styles.detailsText}>{currentQuestion.premissa}</Text>
                </View>
              )}
              
              {currentQuestion.fontes && (
                <View style={styles.detailsContainer}>
                  <Text style={styles.detailsLabel}>Fonte de informações e evidências:</Text>
                  <Text style={styles.detailsText}>{currentQuestion.fontes}</Text>
                </View>
              )}
              
              {currentQuestion.segmento && (
                <View style={styles.segmentoContainer}>
                  <Text style={styles.segmentoLabel}>🎯 Segmento:</Text>
                  <Text style={styles.segmentoText}>{currentQuestion.segmento}</Text>
                </View>
              )}
              
              {/* Resposta Atual */}
              <View style={styles.answerContainer}>
                <Text style={styles.answerLabel}>Resposta:</Text>
                <View style={styles.answerDisplay}>
                  <Text style={[
                    styles.answerText,
                    { color: getAnswerDisplay(currentData?.currentAnswer).color }
                  ]}>
                    {getAnswerDisplay(currentData?.currentAnswer).text}
                  </Text>
                </View>
              </View>
              
              {/* Justificativa para Não se Aplica */}
              {currentData?.currentAnswer === 'nao-se-aplica' && currentData?.notApplicableReason && (
                <View style={styles.notApplicableContainer}>
                  <Text style={styles.notApplicableLabel}>
                    Justificativa para "Não se Aplica":
                  </Text>
                  <Text style={styles.notApplicableText}>
                    {currentData.notApplicableReason}
                  </Text>
                </View>
              )}
              
              {/* Achados */}
              {currentData?.currentAnswer && currentData.currentAnswer !== 'nao-se-aplica' && (
                <View style={styles.findingsContainer}>
                  <Text style={styles.findingsLabel}>
                    📝 Registro dos Achados:
                  </Text>
                  <View style={styles.findingsDisplay}>
                    <Text style={styles.findingsText}>
                      {currentData?.currentFindings || 'Aguardando digitação...'}
                    </Text>
                  </View>
                </View>
              )}
              
              {/* Anexos */}
              {currentData?.currentAttachments && currentData.currentAttachments.length > 0 && (
                <StreamAttachmentComponent 
                  attachments={currentData.currentAttachments}
                />
              )}
            </Card.Content>
          </Card>
        ) : (
          <Card style={styles.waitingCard}>
            <Card.Content>
              <View style={styles.waitingContainer}>
                <Text style={styles.waitingIcon}>⏳</Text>
                <Text style={styles.waitingText}>
                  Aguardando avaliador selecionar próximo requisito...
                </Text>
              </View>
            </Card.Content>
          </Card>
        )}
      </ScrollView>
      
      <FooterComponent />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AVAQualisTheme.colors.background,
  },
  scrollContainer: {
    flex: 1,
  },
  content: {
    padding: AVAQualisTheme.spacing.md,
    paddingBottom: 100,
  },
  
  // Appbar
  appbar: {
    backgroundColor: AVAQualisTheme.colors.primary,
    elevation: 4,
  },
  appbarTitle: {
    color: AVAQualisTheme.colors.onPrimary,
    fontWeight: 'bold',
  },
  appbarSubtitle: {
    color: AVAQualisTheme.colors.onPrimary,
    opacity: 0.9,
  },
  liveBadge: {
    backgroundColor: AVAQualisTheme.colors.error,
    marginRight: AVAQualisTheme.spacing.md,
  },
  
  // Loading e Error
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: AVAQualisTheme.spacing.md,
    fontSize: 16,
    color: AVAQualisTheme.colors.primary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: AVAQualisTheme.spacing.xl,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: AVAQualisTheme.spacing.md,
  },
  errorText: {
    fontSize: 16,
    color: AVAQualisTheme.colors.error,
    textAlign: 'center',
    marginBottom: AVAQualisTheme.spacing.lg,
  },
  retryButton: {
    borderRadius: AVAQualisTheme.borderRadius.md,
  },
  
  // Stream Header
  streamHeader: {
    padding: AVAQualisTheme.spacing.md,
    backgroundColor: AVAQualisTheme.colors.surface,
    borderRadius: AVAQualisTheme.borderRadius.lg,
    elevation: 2,
    marginBottom: AVAQualisTheme.spacing.md,
  },
  streamInfo: {
    gap: AVAQualisTheme.spacing.sm,
  },
  streamTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
  },
  formTitle: {
    fontSize: 16,
    color: AVAQualisTheme.colors.onSurface,
    fontWeight: '600',
  },
  avaliadorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: AVAQualisTheme.spacing.xs,
  },
  avaliadorLabel: {
    fontSize: 14,
    color: AVAQualisTheme.colors.secondary,
  },
  avaliadorName: {
    fontSize: 14,
    fontWeight: '600',
    color: AVAQualisTheme.colors.onSurface,
  },
  streamMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: AVAQualisTheme.spacing.md,
    marginTop: AVAQualisTheme.spacing.sm,
  },
  statusChip: {
    backgroundColor: AVAQualisTheme.colors.surface,
  },
  statusChipActive: {
    backgroundColor: `${AVAQualisTheme.colors.error}20`,
    borderColor: AVAQualisTheme.colors.error,
  },
  expirationText: {
    fontSize: 12,
    color: AVAQualisTheme.colors.secondary,
  },
  viewCount: {
    fontSize: 12,
    color: AVAQualisTheme.colors.secondary,
  },
  
  // Progress Card
  progressCard: {
    marginBottom: AVAQualisTheme.spacing.md,
    backgroundColor: AVAQualisTheme.colors.surface,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: AVAQualisTheme.spacing.sm,
  },
  progressInfo: {
    flex: 1,
  },
  progressText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
  },
  progressSubtext: {
    fontSize: 12,
    color: AVAQualisTheme.colors.secondary,
  },
  progressBadge: {
    backgroundColor: AVAQualisTheme.colors.primary,
  },
  progressBar: {
    height: 8,
    borderRadius: AVAQualisTheme.borderRadius.sm,
  },
  
  // Navigation Card
  navigationCard: {
    marginBottom: AVAQualisTheme.spacing.md,
    backgroundColor: AVAQualisTheme.colors.surface,
  },
  navigationTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: AVAQualisTheme.colors.secondary,
    marginBottom: AVAQualisTheme.spacing.sm,
  },
  questionNavScroll: {
    flexDirection: 'row',
  },
  questionChip: {
    marginRight: AVAQualisTheme.spacing.xs,
    backgroundColor: AVAQualisTheme.colors.surface,
  },
  questionChipAnswered: {
    backgroundColor: `${AVAQualisTheme.colors.success}20`,
    borderColor: AVAQualisTheme.colors.success,
  },
  questionChipActive: {
    backgroundColor: `${AVAQualisTheme.colors.primary}20`,
    borderColor: AVAQualisTheme.colors.primary,
    borderWidth: 2,
  },
  
  // Question Card
  questionCard: {
    backgroundColor: AVAQualisTheme.colors.surface,
    borderLeftWidth: 4,
    borderLeftColor: AVAQualisTheme.colors.primary,
  },
  questionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: AVAQualisTheme.spacing.sm,
    marginBottom: AVAQualisTheme.spacing.md,
  },
  questionCodeChip: {
    backgroundColor: `${AVAQualisTheme.colors.info}15`,
    borderColor: AVAQualisTheme.colors.info,
  },
  essentialBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3CD',
    borderWidth: 1,
    borderColor: '#FFC107',
    borderRadius: AVAQualisTheme.borderRadius.sm,
    paddingHorizontal: AVAQualisTheme.spacing.sm,
    paddingVertical: 2,
  },
  essentialIcon: {
    fontSize: 12,
    marginRight: 2,
  },
  essentialText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#DC3545',
  },
  questionStatus: {
    fontSize: 12,
    color: AVAQualisTheme.colors.info,
    fontStyle: 'italic',
    marginLeft: 'auto',
  },
  requirementLabel: {
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.primary,
  },
  questionTitle: {
    fontSize: 18,
    marginBottom: AVAQualisTheme.spacing.md,
    lineHeight: 26,
    color: AVAQualisTheme.colors.onSurface,
    fontWeight: '700',
  },
  
  // Details
  detailsContainer: {
    marginBottom: AVAQualisTheme.spacing.md,
    padding: AVAQualisTheme.spacing.sm,
    backgroundColor: AVAQualisTheme.colors.surfaceVariant,
    borderRadius: AVAQualisTheme.borderRadius.md,
  },
  detailsLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.secondary,
    marginBottom: AVAQualisTheme.spacing.xs,
  },
  detailsText: {
    fontSize: 14,
    color: AVAQualisTheme.colors.onSurface,
    lineHeight: 20,
  },
  segmentoContainer: {
    marginBottom: AVAQualisTheme.spacing.md,
    padding: AVAQualisTheme.spacing.sm,
    backgroundColor: `${AVAQualisTheme.colors.tertiary}08`,
    borderRadius: AVAQualisTheme.borderRadius.md,
    borderLeftWidth: 3,
    borderLeftColor: AVAQualisTheme.colors.tertiary,
  },
  segmentoLabel: {
    fontSize: 13,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.tertiary,
    marginBottom: AVAQualisTheme.spacing.xs,
  },
  segmentoText: {
    fontSize: 13,
    color: AVAQualisTheme.colors.onSurface,
    lineHeight: 18,
    fontStyle: 'italic',
    fontWeight: '500',
  },
  
  // Answer
  answerContainer: {
    marginTop: AVAQualisTheme.spacing.md,
    marginBottom: AVAQualisTheme.spacing.md,
  },
  answerLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.secondary,
    marginBottom: AVAQualisTheme.spacing.sm,
  },
  answerDisplay: {
    padding: AVAQualisTheme.spacing.md,
    backgroundColor: AVAQualisTheme.colors.surfaceVariant,
    borderRadius: AVAQualisTheme.borderRadius.md,
    borderWidth: 2,
    borderColor: AVAQualisTheme.colors.outline,
  },
  answerText: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // Not Applicable
  notApplicableContainer: {
    marginBottom: AVAQualisTheme.spacing.md,
    padding: AVAQualisTheme.spacing.sm,
    backgroundColor: `${AVAQualisTheme.colors.warning}10`,
    borderRadius: AVAQualisTheme.borderRadius.md,
    borderLeftWidth: 3,
    borderLeftColor: AVAQualisTheme.colors.warning,
  },
  notApplicableLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: AVAQualisTheme.colors.warning,
    marginBottom: AVAQualisTheme.spacing.xs,
  },
  notApplicableText: {
    fontSize: 14,
    color: AVAQualisTheme.colors.onSurface,
    fontStyle: 'italic',
  },
  
  // Findings
  findingsContainer: {
    marginBottom: AVAQualisTheme.spacing.md,
  },
  findingsLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: AVAQualisTheme.colors.secondary,
    marginBottom: AVAQualisTheme.spacing.sm,
  },
  findingsDisplay: {
    padding: AVAQualisTheme.spacing.md,
    backgroundColor: AVAQualisTheme.colors.surfaceVariant,
    borderRadius: AVAQualisTheme.borderRadius.md,
    minHeight: 100,
  },
  findingsText: {
    fontSize: 14,
    color: AVAQualisTheme.colors.onSurface,
    lineHeight: 20,
  },
  
  // Waiting Card
  waitingCard: {
    backgroundColor: AVAQualisTheme.colors.surface,
  },
  waitingContainer: {
    alignItems: 'center',
    padding: AVAQualisTheme.spacing.xl,
  },
  waitingIcon: {
    fontSize: 48,
    marginBottom: AVAQualisTheme.spacing.md,
  },
  waitingText: {
    fontSize: 16,
    color: AVAQualisTheme.colors.secondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});